import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { DropResult } from "@hello-pangea/dnd";
import LayoutTabs from "@/components/controls/LayoutTabs";
import NotFoundPage from "@/components/layouts/NotFoundPage";
import { Button } from "@/components/ui/button";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import Text from "@/components/ui/text";
import { BundlesTab } from "./components/BundlesTab";
import { ProductsTopBar } from "./components/ProductsTopBar";
import { ServiceItemTable } from "./components/ServiceItemTable";
import { ServicesEmptyState } from "./components/ServicesEmptyState";
import { useUpdateServiceItemsOrder } from "./hooks";
import { useProductEditor } from "./hooks/useProductEditor";
import { useProductList } from "./hooks/useProductList";
import { useServiceItemActions } from "./hooks/useServiceItemActions";
import { PRODUCTS_SETTINGS_PATH } from "./consts";
import { ServiceItem } from "./types";

const TRANSLATION_KEY = "settings.product";

enum ProductTab {
  SERVICES = "services",
  BUNDLES = "bundles"
}

export const ProductPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { productId } = useParams();
  const { products, isLoading, refetch } = useProductList();
  const updateServiceItemsOrderMutation = useUpdateServiceItemsOrder();
  const [selectedTab, setSelectedTab] = useState<string>(ProductTab.SERVICES);

  const productIndex = products.findIndex(({ id }) => id === productId);
  const product = productIndex >= 0 ? products[productIndex] : undefined;

  // Shows a dragged order until the products refetched after the save replace it.
  const [draggedOrder, setDraggedOrder] = useState<ServiceItem[] | null>(null);
  useEffect(() => {
    setDraggedOrder(null);
  }, [product]);

  const { openEditProduct, dialogs: productDialogs } = useProductEditor({
    products,
    refetchProducts: refetch,
    onDeleted: () => navigate(PRODUCTS_SETTINGS_PATH)
  });
  const {
    openAddServiceItem,
    openEditServiceItem,
    requestDeleteServiceItem,
    dialogs: serviceItemDialogs
  } = useServiceItemActions(refetch);

  if (!product) {
    return isLoading ? null : <NotFoundPage data-test="product-page-404" />;
  }

  const serviceItems = draggedOrder ?? product.serviceItems;

  const handleDragEnd = (result: DropResult) => {
    const { destination, source } = result;
    if (!destination || destination.index === source.index) return;
    // A second save could land before the first and leave the older order.
    if (updateServiceItemsOrderMutation.isPending) return;

    const reordered = [...serviceItems];
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);
    setDraggedOrder(
      reordered.map((item, index) => ({ ...item, itemOrder: index }))
    );

    const items: Record<number, string> = {};
    reordered.forEach((item, index) => {
      items[index] = item.id;
    });
    updateServiceItemsOrderMutation.mutate(
      { pathParams: { productId: product.id }, data: { items } },
      { onError: () => setDraggedOrder(null) }
    );
  };

  return (
    <div className="flex w-full flex-col" data-test="product-page">
      <ProductsTopBar current={product.name} />
      <div className="mb-4 flex w-full items-center justify-center">
        <div className="flex w-[800px] flex-col items-start gap-8 pt-12">
          <div className="flex w-full items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-4">
              <LogoIconContainer index={productIndex} />
              <div className="flex min-w-0 flex-col">
                <Text
                  variant="title3H1"
                  className="normal-case"
                  data-test="product-page-name"
                >
                  {product.name}
                </Text>
                <Text
                  variant="bodySecondary"
                  data-test="product-page-description"
                >
                  {product.description}
                </Text>
              </div>
            </div>
            <Button
              variant="tertiary"
              onClick={() => openEditProduct(product)}
              data-test="product-page-edit-button"
            >
              {t(`${TRANSLATION_KEY}.productPage.editProduct`)}
            </Button>
          </div>
          <div className="flex w-full flex-col gap-6">
            <LayoutTabs
              options={{
                [ProductTab.SERVICES]: t(
                  `${TRANSLATION_KEY}.productPage.tabs.services`
                ),
                [ProductTab.BUNDLES]: t(
                  `${TRANSLATION_KEY}.productPage.tabs.bundles`
                )
              }}
              defaultValue={selectedTab}
              valueSelected={setSelectedTab}
            />
            {selectedTab === ProductTab.BUNDLES ? (
              <BundlesTab product={product} />
            ) : serviceItems.length === 0 ? (
              <ServicesEmptyState
                onAddService={() => openAddServiceItem(product.id)}
              />
            ) : (
              <div
                className="overflow-hidden rounded-2xl border border-border-border1 bg-surface-elevation1"
                data-test="product-page-services"
              >
                <ServiceItemTable
                  serviceItems={serviceItems}
                  onDragEnd={handleDragEnd}
                  onEditServiceItem={openEditServiceItem}
                  onDeleteServiceItem={requestDeleteServiceItem}
                  onAddServiceItem={() => openAddServiceItem(product.id)}
                  withTopBorder={false}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {productDialogs}
      {serviceItemDialogs}
    </div>
  );
};
